/**
 * InlineRef — 作用域感知的 $ref{} 解析 + 显示。
 *
 * 解析顺序：
 * 1. 循环变量 ($each / $each_index / $each.field) — 通过 ScopeContext.loopVar
 * 2. Scope chain 查找 output — findOutputInScope()
 * 3. 根级 outputs — DocOutputsContext
 */

import {
    getContentMemberValue,
    splitScopedMemberPath,
} from "@magic-md/core";
import { useT } from "@magic-md/i18n/react";
import { Copy, Link2 } from "lucide-react";
import {
    findContentDefinitionInScope,
    findDefinitionInScope,
    findForDefinitionInScope,
    findLoopVarInScope,
    findOutputInScope,
    useScopeContext,
} from "../../ScopeContext";
import type { CSSProperties, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import {
    ContextMenu,
    type ContextMenuItem,
    type ContextMenuProps,
    useContextMenu,
} from "../../ContextMenu";
import { useDocContext, useDocOutput } from "../contexts";
import { formatRefValue, resolveRefValue } from "../utils";
import { resolveLoopVarRef } from "../utils/loop-vars";

type InlineRefVariant = "inline" | "heading";
type InlineRefDataAttrs = { [key: `data-${string}`]: string | undefined };

const inlineRefClassByVariant: Record<InlineRefVariant, string> = {
    inline: "inline align-baseline",
    heading: "inline align-baseline",
};

const inheritedInlineTextStyle: CSSProperties = {
    font: "inherit",
    letterSpacing: "inherit",
    lineHeight: "inherit",
    overflowWrap: "break-word",
    wordBreak: "normal",
};

const inlineRefResolvedStyle: CSSProperties = {
    ...inheritedInlineTextStyle,
    color: "inherit",
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationThickness: "0.08em",
    textDecorationColor: "var(--content-inline-ref-border)",
    textUnderlineOffset: "0.16em",
};

const inlineRefWarningStyle: CSSProperties = {
    ...inheritedInlineTextStyle,
    color: "var(--content-inline-ref-warning-foreground)",
    cursor: "help",
    textDecorationLine: "underline",
    textDecorationStyle: "dotted",
    textDecorationThickness: "0.08em",
    textDecorationColor: "currentColor",
    textUnderlineOffset: "0.16em",
};

async function writeClipboardText(text: string): Promise<boolean> {
    if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            return true;
        } catch {
            // Fall through to the DOM fallback below.
        }
    }

    if (typeof document === "undefined" || typeof document.execCommand !== "function") {
        return false;
    }

    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.opacity = "0";

    try {
        document.body.appendChild(textarea);
        textarea.select();
        return document.execCommand("copy");
    } catch {
        return false;
    } finally {
        textarea.remove();
    }
}

export function InlineRef({
    definitionId,
    accessor,
    outputPath,
    refPath,
    variant = "inline",
}: {
    definitionId: string;
    accessor: "output" | "body" | "unit";
    outputPath: string;
    refPath: string;
    variant?: InlineRefVariant;
}) {
    const t = useT("editor");
    const doc = useDocContext();
    const scope = useScopeContext();
    const outputRootId = getOutputRootUnitId(doc, scope, definitionId);
    const rootOutput = useDocOutput(outputRootId);
    const isBodyRef = accessor === "body";
    const outputRequestKeyRef = useRef<string | null>(null);
    const externalRef = accessor === "output"
        ? getExternalRefForOutputUnit(doc, outputRootId)
        : undefined;

    useEffect(() => {
        if (accessor !== "output" || rootOutput !== undefined || !externalRef || !doc.onLoadOutput) {
            return;
        }
        const requestKey = `${outputRootId}\u0000${externalRef.protocol}\u0000${externalRef.locator}`;
        if (outputRequestKeyRef.current === requestKey) return;
        outputRequestKeyRef.current = requestKey;
        doc.onLoadOutput(outputRootId).catch(() => {});
    }, [accessor, doc.onLoadOutput, externalRef, outputRootId, rootOutput]);

    if (accessor === "unit") {
        const found = hasUnitInScope(doc, scope, definitionId);
        return (
            found
                ? (
                    <CopyableInlineRef
                        variant={variant}
                        style={inlineRefResolvedStyle}
                        title={`$ref{${refPath}}`}
                        copyValue={definitionId}
                        refPath={refPath}
                    >
                        {definitionId}
                    </CopyableInlineRef>
                )
                : (
                    <InlineRefWarning
                        variant={variant}
                        title={t("render.inlineRef.unitNotFound", { ref: `$ref{${refPath}}` })}
                        label={refPath}
                    />
                )
        );
    }

    // 1. 循环变量引用 $ref{$each...}
    if (refPath.startsWith("$")) {
        const loopVar = findLoopVarInScope(scope);
        if (loopVar) {
            const resolved = resolveLoopVarRef(refPath, loopVar);
            if (resolved !== undefined) {
                const display = formatRefValue(resolved);
                return (
                    <CopyableInlineRef
                        variant={variant}
                        style={inlineRefResolvedStyle}
                        title={`$ref{${refPath}}`}
                        copyValue={display}
                        refPath={refPath}
                    >
                        {display}
                    </CopyableInlineRef>
                );
            }
        }
    }

    // 2. Scope chain output lookup
    const scopedOutput = findOutputInScope(scope, definitionId);

    // Merge scope outputs with root outputs for resolveRefValue
    const mergedOutputs = new Map<string, PluginOutput>();
    if (rootOutput !== undefined) {
        mergedOutputs.set(outputRootId, rootOutput);
    }
    if (scope) {
        for (const [id, output] of scopeOutputEntries(scope)) {
            mergedOutputs.set(id, output);
        }
    }

    // Merge content definitions
    const mergedContentDefs = scope
        ? mergeContentDefs(doc.contentDefinitions, scope)
        : doc.contentDefinitions;

    // 3. 正常解析
    const { resolved, value } = resolveRefValue(
        definitionId,
        accessor,
        outputPath,
        mergedOutputs,
        mergedContentDefs,
    );
    const hasOutput = isBodyRef
        ? hasContentBodyTarget(definitionId, mergedContentDefs)
        : scopedOutput !== undefined
            || rootOutput !== undefined
            || hasOutputTarget(definitionId, mergedOutputs, mergedContentDefs);

    if (!resolved) {
        const title = isBodyRef
            ? t("render.inlineRef.contentNotFound", { ref: `$ref{${refPath}}` })
            : hasOutput
                ? t("render.inlineRef.pathMissing", { ref: `$ref{${refPath}}` })
                : t("render.inlineRef.notExecuted", { ref: `$ref{${refPath}}` });
        return <InlineRefWarning variant={variant} title={title} label={refPath} />;
    }

    const display = formatRefValue(value);

    return (
        <CopyableInlineRef
            variant={variant}
            style={inlineRefResolvedStyle}
            title={`$ref{${refPath}}`}
            copyValue={display}
            refPath={refPath}
        >
            {display}
        </CopyableInlineRef>
    );
}

/* ── helpers ── */

import type { ContentDefinition, ExternalOutputDescriptor, PluginOutput } from "@magic-md/core";
import type { ScopeContextValue } from "../../ScopeContext";

function getExternalRefForOutputUnit(
    doc: ReturnType<typeof useDocContext>,
    unitId: string,
): ExternalOutputDescriptor | undefined {
    const definitionRef = doc.definitions.get(unitId)?.externalRef;
    if (definitionRef) return definitionRef;

    const contentRef = doc.contentDefinitions?.get(unitId)?.externalRef;
    if (contentRef) return contentRef;

    const forRef = doc.forBlocksMap?.get(unitId)?.externalRef;
    if (forRef) return forRef;

    const useBindingRef = doc.useBindings?.find((binding) => binding.id === unitId)?.externalRef;
    if (useBindingRef) return useBindingRef;

    const importRef = doc.importBindings?.find((binding) => binding.namespace === unitId)?.externalRef;
    return importRef;
}

function getOutputRootUnitId(
    doc: ReturnType<typeof useDocContext>,
    scope: ScopeContextValue | null,
    unitId: string,
): string {
    const scoped = splitScopedMemberPath(
        unitId,
        (id) =>
            !!findContentDefinitionInScope(scope, id) ||
            !!doc.contentDefinitions?.has(id),
        (id) =>
            !!findDefinitionInScope(scope, id) ||
            !!findForDefinitionInScope(scope, id) ||
            !!findContentDefinitionInScope(scope, id) ||
            doc.definitions.has(id) ||
            !!doc.forBlocksMap?.has(id) ||
            !!doc.contentDefinitions?.has(id),
    );
    return scoped?.rootId ?? unitId;
}

function hasContentBodyTarget(
    definitionId: string,
    contentDefinitions: Map<string, ContentDefinition> | undefined,
): boolean {
    if (contentDefinitions?.has(definitionId)) return true;
    const scoped = splitScopedMemberPath(
        definitionId,
        (id) => !!contentDefinitions?.has(id),
        (id) => !!contentDefinitions?.has(id),
    );
    if (!scoped) return false;
    return getContentMemberValue(contentDefinitions?.get(scoped.rootId)?.output, scoped.memberPath) !== undefined;
}

function hasOutputTarget(
    definitionId: string,
    outputs: Map<string, PluginOutput>,
    contentDefinitions: Map<string, ContentDefinition> | undefined,
): boolean {
    if (outputs.has(definitionId)) return true;
    if (contentDefinitions?.get(definitionId)?.output !== undefined) return true;
    const scoped = splitScopedMemberPath(
        definitionId,
        (id) => !!contentDefinitions?.has(id),
        (id) => outputs.has(id) || !!contentDefinitions?.has(id),
    );
    if (!scoped) return false;
    return getContentMemberValue(contentDefinitions?.get(scoped.rootId)?.output, scoped.memberPath) !== undefined;
}

function InlineRefWarning({
    variant,
    title,
    label,
}: {
    variant: InlineRefVariant;
    title: string;
    label: string;
}) {
    return (
        <CopyableInlineRef
            variant={variant}
            style={inlineRefWarningStyle}
            title={title}
            ariaLabel={title}
            copyValue={label}
            refPath={label}
            data-md-ref-status="warning"
            data-md-ref-message={title}
        >
            <span
                aria-hidden="true"
                style={{
                    color: "var(--status-warning)",
                    marginInlineEnd: "0.25em",
                }}
            >
                ⚠
            </span>
            <span>{label}</span>
        </CopyableInlineRef>
    );
}

function CopyableInlineRef({
    variant,
    title,
    ariaLabel,
    copyValue,
    refPath,
    style,
    children,
    ...dataAttrs
}: {
    variant: InlineRefVariant;
    title: string;
    ariaLabel?: string;
    copyValue: string;
    refPath: string;
    style: CSSProperties;
    children: ReactNode;
} & InlineRefDataAttrs) {
    const t = useT("editor");
    const contextMenu = useContextMenu();
    const copyReference = `$ref{${refPath}}`;
    const copyText = useCallback((text: string) => {
        void writeClipboardText(text);
    }, []);
    const menuItems = useMemo<ContextMenuItem[]>(
        () => [
            {
                key: "copy-value",
                label: t("common.copy"),
                icon: <Copy size={13} />,
                onClick: () => copyText(copyValue),
            },
            {
                key: "copy-reference",
                label: t("copy.reference"),
                icon: <Copy size={13} />,
                onClick: () => copyText(copyReference),
            },
            {
                key: "copy-path",
                label: t("copy.path"),
                icon: <Link2 size={13} />,
                onClick: () => copyText(refPath),
            },
        ],
        [copyReference, copyText, copyValue, refPath, t],
    );

    return (
        <>
            <span
                className={inlineRefClassByVariant[variant]}
                style={{ ...style, cursor: "context-menu" }}
                title={title}
                aria-label={ariaLabel}
                onContextMenu={contextMenu.handler}
                {...dataAttrs}
            >
                {children}
            </span>
            <InlineRefContextMenu {...contextMenu.props} items={menuItems} />
        </>
    );
}

function InlineRefContextMenu(props: ContextMenuProps) {
    if (!props.open) return null;
    if (typeof document === "undefined") return null;
    return createPortal(<ContextMenu {...props} />, document.body);
}

function scopeOutputEntries(
    scope: ScopeContextValue | null,
): [string, PluginOutput][] {
    const entries: [string, PluginOutput][] = [];
    let current = scope;
    while (current) {
        for (const [k, v] of Object.entries(current.unitOutputs)) {
            entries.push([k, v]);
        }
        current = current.parent;
    }
    return entries;
}

function mergeContentDefs(
    root: Map<string, ContentDefinition> | undefined,
    scope: ScopeContextValue | null,
): Map<string, ContentDefinition> | undefined {
    if (!scope) return root;
    const merged = new Map(root ?? []);
    let current: ScopeContextValue | null = scope;
    while (current) {
        for (const [k, v] of current.contentDefinitions) {
            merged.set(k, v);
        }
        current = current.parent;
    }
    return merged.size > 0 ? merged : root;
}

function hasUnitInScope(
    doc: ReturnType<typeof useDocContext>,
    scope: ScopeContextValue | null,
    unitId: string,
): boolean {
    if (findDefinitionInScope(scope, unitId)) return true;
    if (findForDefinitionInScope(scope, unitId)) return true;
    if (findContentDefinitionInScope(scope, unitId)) return true;
    if (doc.definitions.has(unitId)) return true;
    if (doc.contentDefinitions?.has(unitId)) return true;
    if (doc.forBlocksMap?.has(unitId)) return true;
    if (doc.importDocuments?.has(unitId)) return true;
    return false;
}
